import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Skeleton, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Alert,
} from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAgentList } from '../hooks/useAgentPrompts'
import { adminAgentsAPI } from '../../../services/api'

export default function AgentSidebar({ refreshKey = 0, collapsed = false, onToggle }) {
  const navigate = useNavigate()
  const { agentName } = useParams()
  const { agents, loading } = useAgentList(refreshKey)
  const [hoveredAgent, setHoveredAgent] = useState(null)
  const [deleteCandidate, setDeleteCandidate] = useState(null) // { name, parentAgent }
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const { orchestrators, subAgentsByParent, unparented } = useMemo(() => {
    const orchestrators = agents.filter(a => !a.is_sub_agent)
    const subAgentsByParent = {}
    const unparented = []
    agents.filter(a => a.is_sub_agent).forEach(a => {
      if (a.parent_agent) {
        if (!subAgentsByParent[a.parent_agent]) subAgentsByParent[a.parent_agent] = []
        subAgentsByParent[a.parent_agent].push(a)
      } else {
        unparented.push(a)
      }
    })
    return { orchestrators, subAgentsByParent, unparented }
  }, [agents])

  const handleDeleteConfirm = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await adminAgentsAPI.deleteAgent(deleteCandidate.name)
      setDeleteCandidate(null)
      if (agentName === deleteCandidate.name) {
        navigate('/admin/agent-builder')
      } else {
        navigate(0) // reload to refresh the agent list
      }
    } catch {
      setDeleteError('Failed to delete agent')
      setDeleting(false)
    }
  }

  const renderItem = (agent, isSubAgent) => {
    const url = `/admin/agent-builder/${encodeURIComponent(agent.name)}`
    return (
      <ListItemButton
        key={agent.name}
        selected={agent.name === agentName}
        onClick={() => navigate(url)}
        onMouseEnter={() => setHoveredAgent(agent.name)}
        onMouseLeave={() => setHoveredAgent(null)}
        sx={{ borderRadius: 1, mx: 0.5, pl: isSubAgent ? 3 : 1, pr: 0.5 }}
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          {isSubAgent
            ? <AccountTreeIcon fontSize="small" color={agent.name === agentName ? 'primary' : 'action'} />
            : <SmartToyIcon fontSize="small" color={agent.name === agentName ? 'primary' : 'inherit'} />}
        </ListItemIcon>
        <ListItemText
          primary={agent.name.replace(/_/g, ' ')}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
        />
        {hoveredAgent === agent.name && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteCandidate({ name: agent.name, parentAgent: agent.parent_agent || null, subAgentCount: subAgentsByParent[agent.name]?.length || 0 })
            }}
            sx={{ p: 0.25, color: 'error.main', opacity: 0.7, flexShrink: 0 }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </ListItemButton>
    )
  }

  if (collapsed) {
    return (
      <Box sx={{ width: 28, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 0.5 }}>
        <IconButton size="small" onClick={onToggle} title="Show agents">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Agents
          </Typography>
          <IconButton size="small" onClick={onToggle} title="Hide agents">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        <List dense sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} sx={{ px: 2, py: 0.5 }}>
                  <Skeleton height={36} />
                </Box>
              ))
            : (
              <>
                {orchestrators.map(agent => (
                  <Box key={agent.name}>
                    {renderItem(agent, false)}
                    {subAgentsByParent[agent.name]?.map(sub => renderItem(sub, true))}
                  </Box>
                ))}
                {unparented.map(agent => renderItem(agent, true))}
              </>
            )}
        </List>
      </Box>

      <Dialog open={!!deleteCandidate} onClose={() => !deleting && setDeleteCandidate(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 14 }}>Delete {deleteCandidate?.name}?</DialogTitle>
        <DialogContent>
          {deleteCandidate?.parentAgent && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 11 }}>
              This is a sub-agent of <strong>{deleteCandidate.parentAgent}</strong>. Any <code>@{deleteCandidate.name}</code> references in that agent's prompts will be highlighted as errors after deletion.
            </Alert>
          )}
          {deleteCandidate?.subAgentCount > 0 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 11 }}>
              This agent has <strong>{deleteCandidate.subAgentCount} sub-agent{deleteCandidate.subAgentCount > 1 ? 's' : ''}</strong> which will also be permanently deleted.
            </Alert>
          )}
          <DialogContentText sx={{ fontSize: 12 }}>This action cannot be undone.</DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 1, fontSize: 11 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDeleteCandidate(null)} disabled={deleting}>Cancel</Button>
          <Button size="small" color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
